import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ContentChild,
  ViewChild,
  TemplateRef,
  OnChanges,
  SimpleChanges,
  output,
  input,
  ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import {
  MatPaginator,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import {
  MatTable,
  MatTableDataSource,
  MatTableModule,
} from '@angular/material/table';
import { MatCardModule } from '@angular/material/card';
import { MatTooltipModule } from '@angular/material/tooltip';

export interface FileSeedTableItem {
  id: number;
  version: string;
  createdBy: string;
  createdDate: string;
  status: string;

  error?: string | null;
  errorDate?: string | null;
  gcsInconsistencyCsvUri?: string | null;
  gcsMonitoringShapeUriOutput?: string | null;
  gcsFieldsShapeUriOutput?: string | null;
  isProcessing: boolean;
  isCompleted: boolean;
  isProcessingInput: boolean;
  isCompletedInput: boolean;
  validationSuccess?: boolean | null;
  lastDownloadOutput?: string | null;
  lastUploadInput?: string | null;
  userName: string | null;
  [key: string]: any;
}

export interface FileSeedTableAction {
  label?: string;
  icon: string;
  action: (
    item: FileSeedTableItem,
    fileType: 'fields' | 'monitoring' | 'inconsistency',
  ) => void;
  condition?: (item: FileSeedTableItem) => boolean;
}

export interface FileSeedTableConfig {
  showActionsColumn: boolean;
  columns: string[];
  initialSort?: Sort;
}

@Component({
  selector: 'app-file-seed-table',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatIconModule,
    MatMenuModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatSortModule,
    MatTableModule,
    MatTooltipModule,
  ],
  templateUrl: './file-seed-table.component.html',
  styleUrl: './file-seed-table.component.scss',
  providers: [provideNativeDateAdapter()],
  encapsulation: ViewEncapsulation.None,
})
export class FileSeedTableComponent implements AfterViewInit, OnChanges {
  dataSource = input(new MatTableDataSource<FileSeedTableItem>([]));
  isLoading = input(false);
  length = input(0);
  config = input<FileSeedTableConfig>({
    showActionsColumn: true,
    columns: [
      'id',
      'version',
      'createdBy',
      'createdDate',
      'statusSeed',
      'lastDownloadOutput',
      'statusFileSrc',
      'lastUploadInput',
      'consistency',
      'gcsFieldsShapeUriOutput',
      'userName',

      'error',
      'errorDate',
    ],
    initialSort: { active: 'createdDate', direction: 'desc' },
  });
  actions = input<FileSeedTableAction[]>([]);

  sortChange = output<Sort>();
  pageChange = output<PageEvent>();
  refresh = output<void>();

  @ViewChild(MatSort) declare sort: MatSort;
  @ViewChild(MatPaginator) declare paginator: MatPaginator;
  @ViewChild(MatTable) declare table: MatTable<FileSeedTableItem>;

  get displayedColumns(): string[] {
    const columns = [...this.config().columns];

    if (this.config().showActionsColumn && this.actions().length > 0) {
      columns.push('actions');
    }

    return columns;
  }

  ngAfterViewInit() {
    if (this.sort) {
      const config = this.config();
      if (config.initialSort?.active) {
        this.sort.active = config.initialSort.active;
        this.sort.direction = config.initialSort?.direction ?? 'desc';
      }
      this.dataSource().sort = this.sort;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['dataSource'] && this.sort) {
      this.dataSource().sort = this.sort;
    }
  }

  onSortChange(event: Sort) {
    this.sortChange.emit(event);
  }

  onPageChange(event: PageEvent) {
    this.pageChange.emit(event);
  }

  onRefreshClick(): void {
    this.refresh.emit();
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Em Processamento':
        return 'chip-processing';
      case 'Concluído':
        return 'chip-completed';
      case 'Erro':
        return 'chip-failed';
      case 'Pendente':
        return 'chip-pending';
      default:
        return 'chip-neutral';
    }
  }

  getStatusLabel(item: FileSeedTableItem): string {
    if (item.error) {
      return 'Erro';
    }
    if (item.isProcessing) {
      return 'Em Processamento';
    }
    if (item.isCompleted) {
      return 'Concluído';
    }
    return 'N/A';
  }
  getStatusLabelSRC(item: FileSeedTableItem): string {
    if (item.error) {
      return 'Erro';
    }
    if (item.isProcessingInput) {
      return 'Em Processamento';
    }
    if (item.isCompletedInput) {
      return 'Concluído';
    }
    return 'N/A';
  }

  executeAction(
    action: FileSeedTableAction,
    item: FileSeedTableItem,
    event: Event,
    fileType: 'fields' | 'monitoring' | 'inconsistency',
  ) {
    event.stopPropagation();
    action.action(item, fileType);
  }

  shouldShowAction(
    action: FileSeedTableAction,
    item: FileSeedTableItem,
  ): boolean {
    return !action.condition || action.condition(item);
  }

  getFileName(filePath?: string | null): string {
    if (!filePath) {
      return '';
    }

    // Extrai o nome do arquivo do caminho completo
    const fileName = filePath.split('/').pop() || filePath;
    return fileName;
  }
}
